import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Spin } from 'antd';
import { SendOutlined, HistoryOutlined } from '@ant-design/icons';
import Markdown from 'react-markdown';
import { aiChatApi, ChatMessage, PendingChange } from '~/services/ai-chat';
import { Scratchpad } from './scratchpad';
import { HistoryPanel } from './history-panel';
import { AiFeedbackButton } from './ai-feedback';

export const AskOzScreen = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [confirmedChanges, setConfirmedChanges] = useState<PendingChange[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [scratchpadOpen, setScratchpadOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  // Auto-open scratchpad when new pending changes arrive
  useEffect(() => {
    if (pendingChanges.length > 0) {
      setHistoryOpen(false);
      setScratchpadOpen(true);
    }
  }, [pendingChanges]);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    const userMessage: ChatMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsStreaming(true);

    const assistantMessage: ChatMessage = { role: 'assistant', content: '' };
    setMessages([...updatedMessages, assistantMessage]);

    try {
      let accumulated = '';
      for await (const result of aiChatApi.streamChat(updatedMessages, sessionId)) {
        if (result.sessionId) {
          setSessionId(result.sessionId);
        }
        if (result.pendingChanges) {
          setPendingChanges((prev) => [...prev, ...result.pendingChanges!]);
        }
        if (result.text) {
          accumulated += result.text;
          setMessages([
            ...updatedMessages,
            { role: 'assistant', content: accumulated },
          ]);
        }
      }
    } catch {
      setMessages([
        ...updatedMessages,
        {
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again.',
        },
      ]);
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, messages, sessionId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleConfirm = useCallback(
    async (changes: PendingChange[]) => {
      if (!sessionId || changes.length === 0) return;
      setIsConfirming(true);

      try {
        await aiChatApi.confirmChanges(sessionId, changes);
        setConfirmedChanges((prev) => [...prev, ...changes.map((c) => ({ ...c, confirmed: true }))]);
        setPendingChanges((prev) => prev.filter((p) => !changes.find((c) => c.id === p.id)));

        const summary = changes.map((c) => `- ${c.label}`).join('\n');
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `**Changes applied:**\n${summary}`,
          },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Failed to apply changes. Please try again.',
          },
        ]);
      } finally {
        setIsConfirming(false);
      }
    },
    [sessionId],
  );

  const handleDismiss = useCallback((changeId: string) => {
    setPendingChanges((prev) => prev.filter((c) => c.id !== changeId));
  }, []);

  const toggleHistory = () => {
    if (!historyOpen) {
      setScratchpadOpen(false);
    }
    setHistoryOpen(!historyOpen);
  };

  const toggleScratchpad = () => {
    if (!scratchpadOpen) {
      setHistoryOpen(false);
    }
    setScratchpadOpen(!scratchpadOpen);
  };

  const sidebarOpen = scratchpadOpen || historyOpen;

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Main chat area */}
      <div
        className={`flex flex-col flex-1 transition-all duration-200 ${
          sidebarOpen ? 'mr-[380px]' : ''
        }`}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Ask Oz</h1>
            <p className="text-xs text-gray-500 mt-1">
              AI assistant for estimates, packing guidance, and AHC workflows
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(pendingChanges.length > 0 || confirmedChanges.length > 0) && (
              <button
                onClick={toggleScratchpad}
                className={`text-xs border border-black px-3 py-1 transition-colors ${
                  scratchpadOpen ? 'bg-black text-white' : 'hover:bg-black hover:text-white'
                }`}
              >
                Scratchpad
                {pendingChanges.length > 0 && (
                  <span className="ml-1 bg-red-500 text-white px-1.5 py-0.5 text-[10px]">
                    {pendingChanges.length}
                  </span>
                )}
              </button>
            )}
            <AiFeedbackButton />
            <button
              onClick={toggleHistory}
              className={`text-xs border border-black px-3 py-1 transition-colors flex items-center gap-1 ${
                historyOpen ? 'bg-black text-white' : 'hover:bg-black hover:text-white'
              }`}
            >
              <HistoryOutlined />
              History
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <div className="text-4xl mb-4 font-bold">Oz</div>
              <p className="text-sm">Ask me about pricing, packing, estimates, or anything AHC.</p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-lg w-full">
                {[
                  'Estimate packing for a 48x60" oil painting',
                  'What materials do I need for a sculpture crate?',
                  'Suggest team size for a 50-piece gallery install',
                  'How should I price a blanket-wrap local delivery?',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setInput(suggestion)}
                    className="text-left text-xs px-3 py-2 border border-gray-300 hover:border-black hover:bg-gray-50 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] px-4 py-3 text-sm select-text ${
                  msg.role === 'user'
                    ? 'bg-black text-white whitespace-pre-wrap'
                    : 'bg-gray-100 text-black border border-gray-200 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:mb-1 [&_code]:bg-gray-200 [&_code]:px-1 [&_code]:text-xs [&_pre]:bg-gray-200 [&_pre]:p-2 [&_pre]:overflow-x-auto [&_h1]:text-lg [&_h1]:font-bold [&_h2]:text-base [&_h2]:font-bold [&_h3]:font-bold [&_strong]:font-bold [&_table]:w-full [&_table]:text-xs [&_table]:border-collapse [&_table]:mb-2 [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-200 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-bold [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_hr]:my-2 [&_hr]:border-gray-200'
                }`}
              >
                {msg.role === 'assistant' && (
                  <div className="text-[10px] font-bold text-gray-400 mb-1 uppercase tracking-wider">
                    Oz
                  </div>
                )}
                {msg.content ? (
                  msg.role === 'assistant' ? (
                    <Markdown
                      components={{
                        a: ({ href, children }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {msg.content}
                    </Markdown>
                  ) : (
                    msg.content
                  )
                ) : (isStreaming && i === messages.length - 1 ? (
                  <Spin size="small" />
                ) : null)}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="px-6 py-4 border-t-2 border-black">
          <div className="flex gap-2 items-end">
            <Input.TextArea
              ref={inputRef as any}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Oz a question..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={isStreaming}
              className="flex-1 !border-black !rounded-none"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={isStreaming}
              disabled={!input.trim()}
              className="!bg-black !border-black !rounded-none h-[38px]"
            />
          </div>
          <p className="text-[10px] text-gray-400 mt-2">
            Estimates are approximate. Always review before sending to clients.
          </p>
        </div>
      </div>

      {/* Scratchpad sidebar */}
      <Scratchpad
        pendingChanges={pendingChanges}
        confirmedChanges={confirmedChanges}
        onConfirm={handleConfirm}
        onDismiss={handleDismiss}
        isConfirming={isConfirming}
        isOpen={scratchpadOpen}
        onToggle={toggleScratchpad}
      />

      {/* History sidebar */}
      <HistoryPanel
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
};
